import { useMemo } from "react";
import { useSelector } from "react-redux";
import type {
  AirBookingFlightItinerarySlice,
  BookedFlightItineraryWithDepartureTime,
  FlightItinerarySegment,
} from "@b2bportal/air-booking-api";
import { FlightPostBookingSelectors } from "@b2bportal/core-flights";
import type { ScheduleChangeDiffModel } from "@b2bportal/core-types";
import { getSliceScheduleChangeKey } from "@b2bportal/core-utilities";
import dayjs from "dayjs";

export interface UseFlightScheduleChangeComparisonPanelProps {
  itinerary: BookedFlightItineraryWithDepartureTime;
  diffModel: ScheduleChangeDiffModel;
}

export interface IScheduleChangeSliceComparatorListItem {
  originalSliceSummary: {
    firstSegment: FlightItinerarySegment;
    lastSegment: FlightItinerarySegment;
    totalDurationMinutes: number;
    stopsCount: number;
    displayDestinationCode?: string;
  };
  updatedSliceSummary: {
    firstSegment: FlightItinerarySegment;
    lastSegment: FlightItinerarySegment;
    totalDurationMinutes: number;
    stopsCount: number;
    displayDestinationCode?: string;
  };
  sliceKey: string;
}

const createSliceSummary = (
  slice: AirBookingFlightItinerarySlice,
  isReturn: boolean,
  tripOriginCode: string
) => {
  const segs = slice.segments ?? [];
  const firstSegment = segs[0];
  const lastSegment = segs[segs.length - 1];

  if (!firstSegment || !lastSegment) {
    return null;
  }

  const departureStr =
    firstSegment.zonedScheduledDeparture ??
    firstSegment.scheduledDeparture ??
    "";
  const arrivalStr =
    lastSegment.zonedScheduledArrival ?? lastSegment.scheduledArrival ?? "";
  const totalDurationMinutes =
    departureStr && arrivalStr
      ? dayjs(arrivalStr).diff(dayjs(departureStr), "minutes")
      : 0;

  return {
    firstSegment,
    lastSegment,
    totalDurationMinutes,
    stopsCount: Math.max(0, segs.length - 1),
    displayDestinationCode: isReturn ? tripOriginCode : undefined,
  };
};

export const useFlightScheduleChangeComparisonPanel = (
  props: UseFlightScheduleChangeComparisonPanelProps
) => {
  const { itinerary, diffModel } = props;
  const airports = useSelector(FlightPostBookingSelectors.getAirports);
  const airlines = useSelector(FlightPostBookingSelectors.getAirlines);

  const sliceComparatorList = useMemo(() => {
    const list: IScheduleChangeSliceComparatorListItem[] = [];
    const slices = itinerary.bookedItinerary.travelItinerary.slices;
    const tripOriginCode = slices[0]?.segments?.[0]?.origin?.locationCode ?? "";

    slices.forEach((originalSlice, index) => {
      const sliceKey = getSliceScheduleChangeKey(originalSlice);
      const sliceChange = diffModel.changes[sliceKey];

      if (!sliceChange) return;

      const isReturn = index > 0;
      const originalSummary = createSliceSummary(
        originalSlice,
        isReturn,
        tripOriginCode
      );
      const updatedSummary = createSliceSummary(
        sliceChange.updatedSlice,
        isReturn,
        tripOriginCode
      );

      if (!originalSummary || !updatedSummary) return;

      list.push({
        originalSliceSummary: originalSummary,
        updatedSliceSummary: updatedSummary,
        sliceKey,
      });
    });

    return list;
  }, [itinerary, diffModel]);

  return {
    sliceComparatorList,
    airports,
    airlines,
  };
};
